import { Exam } from 'api/exams/types';
import { Question } from 'api/questions/types';

export interface SplitScenarioEvidence {
  scenarioId: string;
  scenarioTitle?: string;
  sectionNames: string[];
}

export interface GetSplitScenariosResult {
  hasSplit: boolean;
  evidence: SplitScenarioEvidence[];
}

export interface ConsolidationPreviewItem {
  scenarioId: string;
  scenarioTitle?: string;
  targetSectionName: string;
}

export interface SectionBlock {
  type: 'block' | 'single';
  scenarioId?: string;
  questions: Question[];
}

export type ScenarioChoice = 'pending' | 'keep' | 'update';

export type FormSection = { id: number; name: string; isActive: boolean; questions: Question[] };

/** Returns scenario id string whether question.scenarioId is a string or populated object. */
export function getQuestionScenarioId(q: { scenarioId?: unknown }): string | null {
  if (!q.scenarioId) return null;
  const s = q.scenarioId as string | { _id?: string };
  return typeof s === 'string' ? s : s._id ?? null;
}

/** Max length for a scenario title; longer strings are treated as body content. */
const MAX_TITLE_LENGTH = 200;

/** True if the string looks like HTML (contains tags). */
function looksLikeHtml(s: string): boolean {
  return /<[a-z][\s\S]*>/i.test(s);
}

/** Strip HTML tags and trim; returns plain text suitable for a title, or undefined if result is empty or still looks like content. */
function toPlainTitle(raw: string): string | undefined {
  const stripped = raw.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  if (!stripped || looksLikeHtml(stripped)) return undefined;
  return stripped.length <= MAX_TITLE_LENGTH ? stripped : stripped.slice(0, MAX_TITLE_LENGTH).trim() + '…';
}

/**
 * Returns a short scenario title for display (e.g. in block headers).
 * Uses only dedicated title fields; never uses full scenario body/content (q.scenario or s.scenario).
 */
export function getScenarioTitleFromQuestion(q: {
  scenario?: string;
  scenarioTitle?: string;
  scenarioId?: unknown;
}): string | undefined {
  if (q.scenarioTitle && typeof q.scenarioTitle === 'string') {
    const t = toPlainTitle(q.scenarioTitle);
    if (t) return t;
  }
  const s = q.scenarioId as { title?: string; scenario?: string } | undefined;
  if (s && typeof s === 'object' && s.title && typeof s.title === 'string') {
    const t = toPlainTitle(s.title);
    if (t) return t;
  }
  return undefined;
}

export function getSplitScenarios(exam: Exam | undefined): GetSplitScenariosResult {
  const evidence: SplitScenarioEvidence[] = [];
  if (!exam?.sections?.length) return { hasSplit: false, evidence };

  const scenarioToSections = new Map<string, { sectionNames: Set<string>; scenarioTitle?: string }>();

  for (const section of exam.sections) {
    const sectionName = section.name || 'Unnamed section';
    for (const q of section.questions || []) {
      const sid = getQuestionScenarioId(q);
      if (!sid) continue;
      if (!scenarioToSections.has(sid)) {
        scenarioToSections.set(sid, { sectionNames: new Set(), scenarioTitle: getScenarioTitleFromQuestion(q) });
      }
      const entry = scenarioToSections.get(sid)!;
      entry.sectionNames.add(sectionName);
      if (!entry.scenarioTitle) entry.scenarioTitle = getScenarioTitleFromQuestion(q);
    }
  }
  scenarioToSections.forEach(({ sectionNames, scenarioTitle }, scenarioId) => {
    if (sectionNames.size > 1) {
      evidence.push({
        scenarioId,
        scenarioTitle,
        sectionNames: Array.from(sectionNames)
      });
    }
  });

  return { hasSplit: evidence.length > 0, evidence };
}

/** Returns preview of consolidation: for each split scenario, which section it will move to (first section where it appears). */
export function getConsolidationPreview(exam: Exam | undefined): ConsolidationPreviewItem[] {
  const { evidence } = getSplitScenarios(exam);
  if (!evidence.length || !exam?.sections?.length) return [];

  return evidence.map((item) => {
    let targetSectionName = 'Unnamed section';
    for (const section of exam.sections || []) {
      const hasScenario = (section.questions || []).some((q) => getQuestionScenarioId(q) === item.scenarioId);
      if (hasScenario) {
        targetSectionName = section.name || 'Unnamed section';
        break;
      }
    }
    return {
      scenarioId: item.scenarioId,
      scenarioTitle: item.scenarioTitle,
      targetSectionName
    };
  });
}

export function normalizeSectionQuestions(questions: Question[]): Question[] {
  if (!questions?.length) return [];
  const seen = new Map<string, Question[]>();
  const noScenario: Question[] = [];
  const order: string[] = [];

  for (const q of questions) {
    const sid = getQuestionScenarioId(q) ?? '';
    if (!sid) {
      noScenario.push(q);
      continue;
    }
    if (!seen.has(sid)) {
      order.push(sid);
      seen.set(sid, []);
    }
    seen.get(sid)!.push(q);
  }

  const result: Question[] = [];
  for (const sid of order) result.push(...(seen.get(sid) || []));
  result.push(...noScenario);
  return result;
}

export function normalizeExamSections(sections: FormSection[]): FormSection[] {
  if (!sections?.length) return sections;

  const sectionsCopy = sections.map((s) => ({ ...s, questions: [...(s.questions || [])] }));

  const scenarioToFirstSectionIndex = new Map<string, number>();
  for (let i = 0; i < sectionsCopy.length; i++) {
    for (const q of sectionsCopy[i].questions) {
      const sid = getQuestionScenarioId(q);
      if (sid && !scenarioToFirstSectionIndex.has(sid)) scenarioToFirstSectionIndex.set(sid, i);
    }
  }

  scenarioToFirstSectionIndex.forEach((targetIndex, scenarioId) => {
    const toMove: Question[] = [];
    for (let i = 0; i < sectionsCopy.length; i++) {
      if (i === targetIndex) continue;
      const remaining = sectionsCopy[i].questions.filter((q) => getQuestionScenarioId(q) !== scenarioId);
      const moved = sectionsCopy[i].questions.filter((q) => getQuestionScenarioId(q) === scenarioId);
      sectionsCopy[i].questions = remaining;
      toMove.push(...moved);
    }
    sectionsCopy[targetIndex].questions.push(...toMove);
  });

  for (const s of sectionsCopy) {
    s.questions = normalizeSectionQuestions(s.questions);
  }

  return sectionsCopy;
}

export function sectionQuestionsToBlocks(questions: Question[], _scenarioChoice?: ScenarioChoice): SectionBlock[] {
  if (!questions?.length) return [];
  const blocks: SectionBlock[] = [];
  let i = 0;
  while (i < questions.length) {
    const q = questions[i];
    const sid = getQuestionScenarioId(q);
    if (!sid) {
      blocks.push({ type: 'single', questions: [q] });
      i++;
      continue;
    }
    const group: Question[] = [q];
    i++;
    while (i < questions.length && getQuestionScenarioId(questions[i]) === sid) {
      group.push(questions[i]);
      i++;
    }
    blocks.push({ type: 'block', scenarioId: sid, questions: group });
  }
  return blocks;
}

export function blocksToSectionQuestions(blocks: SectionBlock[]): Question[] {
  return (blocks || []).flatMap((b) => b.questions);
}

/** Reorder questions within a block; returns new questions array for that section. */
export function reorderQuestionsInBlock(
  sectionQuestions: Question[],
  blockIndex: number,
  fromIndex: number,
  toIndex: number
): Question[] {
  const blocks = sectionQuestionsToBlocks(sectionQuestions);
  const block = blocks[blockIndex];
  if (!block || block.type !== 'block' || fromIndex === toIndex) return sectionQuestions;
  const reordered = [...block.questions];
  const [removed] = reordered.splice(fromIndex, 1);
  reordered.splice(toIndex, 0, removed);
  const newBlocks = blocks.map((b, i) => (i === blockIndex ? { ...b, questions: reordered } : b));
  return blocksToSectionQuestions(newBlocks);
}

/** Returns the first section index that contains at least one question with the given scenarioId, or null. */
export function getSectionIndexWithScenario(
  sections: { questions: Question[] }[],
  scenarioId: string | null
): number | null {
  if (!scenarioId || !sections?.length) return null;
  for (let i = 0; i < sections.length; i++) {
    const hasScenario = (sections[i].questions || []).some((q) => getQuestionScenarioId(q) === scenarioId);
    if (hasScenario) return i;
  }
  return null;
}
